/**
 * Service layer for scheduled catalog operations — one function per
 * `CatalogOpsScheduleController` endpoint, each returning mapped UI types.
 *
 * Nothing here sends a tenant: the subsidiary is resolved server-side from the
 * bearer token (the interceptor's `X-Subsidiary-ID` is an accepted override
 * only). A schedule belonging to another tenant answers 404, never 403.
 */

import { catalogOperationsClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';

import {
  toScheduleDetail,
  toSchedulePreview,
  toScheduleRunsPage,
  toSchedulesOverview,
  toSchedulesPage,
  toSchedulesRibbon,
  toScheduleSummary,
} from '../mappers/catalog-ops-schedule.mapper';
import type {
  CreateScheduleRequestWire,
  ScheduleDetailWire,
  SchedulePreviewRequestWire,
  SchedulePreviewResponseWire,
  ScheduleRunsPageWire,
  ScheduleSourceWire,
  ScheduleSpecWire,
  ScheduleSummaryWire,
  ScheduleTriggerResponseWire,
  SchedulesOverviewWire,
  SchedulesPageWire,
  SchedulesRibbonWire,
  UpdateScheduleConfigRequestWire,
  UpdateScheduleRequestWire,
} from '../types/catalog-ops-schedule.api.types';
import type {
  ScheduleDetail,
  ScheduleListFilters,
  SchedulePreview,
  ScheduleRunsPage,
  ScheduleSummary,
  SchedulesOverview,
  SchedulesPage,
  SchedulesRibbon,
} from '../types/catalog-ops-schedule.types';

/** Cross-schedule run-feed filters. `status` matches `ledgerStatus`, not the
 *  tracking layer's `run.status`. */
export interface ScheduleRunFeedFilters {
  status?: string | undefined;
  triggerType?: string | undefined;
  connector?: string | undefined;
  scheduleId?: string | undefined;
  /** ISO instants — both windowed on `startedAt`. */
  since?: string | undefined;
  until?: string | undefined;
  cursor?: string | undefined;
  size?: number | undefined;
}

/** Drops empty filters so the request never sends `status=` and trips the
 *  server's enum parsing (an unrecognized value is a 400, not an ignored one). */
function toSearchParams(filters: Record<string, string | number | undefined>) {
  const params: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === '') continue;
    params[key] = value;
  }
  return params;
}

// ---------------------------------------------------------------------------
// Setting up
// ---------------------------------------------------------------------------

/**
 * POST /preview — dry-runs a spec: the resolved config (typed as `runConfig`), its
 * fingerprint, the duplicate flag and the upcoming fire times. Drives the create form, and
 * is the cheapest way to validate a `jobId`.
 *
 * `source` is either a batch (`{ jobId, runConfig? }`) or the wizard's whole setup
 * (`{ connectorCode, useCase, fromJobId?, runConfig }`); the overrides are evaluated too, so
 * the duplicate check and the 400s match what create would do.
 *
 * `spec` is optional: omit it to fetch the config and duplicate check alone
 * (the response then carries an empty `nextRunPreview`), which is what lets the
 * form render before the user has picked a cadence.
 */
export async function previewSchedule(
  source: ScheduleSourceWire,
  spec?: ScheduleSpecWire,
): Promise<SchedulePreview> {
  const json: SchedulePreviewRequestWire = spec ? { ...source, scheduleSpec: spec } : source;
  const wire = await catalogOperationsClient
    .post(endpoints.catalogOpsSchedules.preview(), { json })
    .json<SchedulePreviewResponseWire>();
  return toSchedulePreview(wire);
}

/** POST / — creates the schedule (201, status ACTIVE), from a `jobId` or from the wizard's
 *  connector + `runConfig`. Temporal owns the timer from this moment.
 *
 *  The response's `recentRuns` / `successRate` / `runCount` /
 *  `productsEnriched` are empty or zero on every write-side response — a
 *  just-created schedule has no history. Read real numbers from the list. */
export async function createSchedule(payload: CreateScheduleRequestWire): Promise<ScheduleSummary> {
  const wire = await catalogOperationsClient
    .post(endpoints.catalogOpsSchedules.root(), { json: payload })
    .json<ScheduleSummaryWire>();
  return toScheduleSummary(wire);
}

/** PUT /{id} — replaces cadence, overlap policy, name and catch-up window. The
 *  config snapshot is untouched; a run already in flight is unaffected. */
export async function updateSchedule(
  scheduleId: string,
  payload: UpdateScheduleRequestWire,
): Promise<ScheduleSummary> {
  const wire = await catalogOperationsClient
    .put(endpoints.catalogOpsSchedules.byId(scheduleId), { json: payload })
    .json<ScheduleSummaryWire>();
  return toScheduleSummary(wire);
}

/** PATCH /{id}/config — partial update over the frozen config snapshot: any `runConfig` field,
 *  plus an optional `reason`; omitted fields are left unchanged. Re-freezes the snapshot and
 *  re-bakes the Temporal schedule, so the change lands on the next fire. Answers the summary,
 *  not the detail: read `GET /{id}` again for the new `runConfig`.
 *
 *  409 here is the same duplicate-fingerprint rule that guards create: the
 *  edited config now matches another active schedule. */
export async function updateScheduleConfig(
  scheduleId: string,
  payload: UpdateScheduleConfigRequestWire,
): Promise<ScheduleSummary> {
  const wire = await catalogOperationsClient
    .patch(endpoints.catalogOpsSchedules.config(scheduleId), { json: payload })
    .json<ScheduleSummaryWire>();
  return toScheduleSummary(wire);
}

/** POST /{id}/pause — stops future fires, keeping the schedule and its history. */
export async function pauseSchedule(scheduleId: string): Promise<ScheduleSummary> {
  const wire = await catalogOperationsClient
    .post(endpoints.catalogOpsSchedules.pause(scheduleId))
    .json<ScheduleSummaryWire>();
  return toScheduleSummary(wire);
}

/** POST /{id}/resume — re-enables fires. */
export async function resumeSchedule(scheduleId: string): Promise<ScheduleSummary> {
  const wire = await catalogOperationsClient
    .post(endpoints.catalogOpsSchedules.resume(scheduleId))
    .json<ScheduleSummaryWire>();
  return toScheduleSummary(wire);
}

/** DELETE /{id} — soft delete (204): pauses, drops the Temporal handle, marks
 *  the row DELETED. History survives and the row leaves the list, which is why
 *  the UI confirms this as "stop and archive", not "permanently delete". */
export async function deleteSchedule(scheduleId: string): Promise<void> {
  await catalogOperationsClient.delete(endpoints.catalogOpsSchedules.byId(scheduleId));
}

/** POST /{id}/trigger — "Run now" (202). Bypasses the overlap policy entirely,
 *  so it 409s while a run is in flight rather than queueing behind it. */
export async function triggerSchedule(scheduleId: string): Promise<string> {
  const wire = await catalogOperationsClient
    .post(endpoints.catalogOpsSchedules.trigger(scheduleId))
    .json<ScheduleTriggerResponseWire>();
  return wire.launcherWorkflowId;
}

// ---------------------------------------------------------------------------
// Viewing
// ---------------------------------------------------------------------------

/** GET / — one cursor-paged page of schedule rows. `DELETED` rows are always
 *  excluded, filter or not. Server ordering is a stable `updated_at desc`. */
export async function getSchedules(filters: ScheduleListFilters = {}): Promise<SchedulesPage> {
  const wire = await catalogOperationsClient
    .get(endpoints.catalogOpsSchedules.root(), { searchParams: toSearchParams({ ...filters }) })
    .json<SchedulesPageWire>();
  return toSchedulesPage(wire);
}

/** GET /ribbon — aggregates over the ENTIRE filtered set, not the loaded page.
 *  Always call it with the same filters as `getSchedules`, and never derive
 *  these numbers by summing rows: that only reflects page one, and the totals
 *  visibly change as the user pages. */
export async function getSchedulesRibbon(
  filters: ScheduleListFilters = {},
): Promise<SchedulesRibbon> {
  // Named explicitly rather than spread: `cursor` and `size` are paging
  // concerns the ribbon has no notion of, and sending them would be noise.
  const { status, connector, search, health } = filters;
  const wire = await catalogOperationsClient
    .get(endpoints.catalogOpsSchedules.ribbon(), {
      searchParams: toSearchParams({ status, connector, search, health }),
    })
    .json<SchedulesRibbonWire>();
  return toSchedulesRibbon(wire);
}

/** GET /overview — dashboard rollup. A single windowed query, so it is safe to
 *  call regardless of run volume. `needsAttention.reason` is server-authored
 *  copy: render it directly. */
export async function getSchedulesOverview(): Promise<SchedulesOverview> {
  const wire = await catalogOperationsClient
    .get(endpoints.catalogOpsSchedules.overview())
    .json<SchedulesOverviewWire>();
  return toSchedulesOverview(wire);
}

/** GET /{id} — durable row plus the live Temporal block. Never 500s on a
 *  Temporal outage: `live.source` comes back `UNAVAILABLE` with the live fields
 *  empty, so branch on that flag instead of reading "no upcoming runs". */
export async function getScheduleDetail(scheduleId: string): Promise<ScheduleDetail> {
  const wire = await catalogOperationsClient
    .get(endpoints.catalogOpsSchedules.byId(scheduleId))
    .json<ScheduleDetailWire>();
  return toScheduleDetail(wire);
}

/** GET /{id}/runs — run history for one schedule, newest-first and cursor-paged.
 *  `scheduleId` / `scheduleName` are null on this feed (they only populate on
 *  the cross-schedule one). */
export async function getScheduleRuns(
  scheduleId: string,
  params: { cursor?: string; size?: number } = {},
): Promise<ScheduleRunsPage> {
  const wire = await catalogOperationsClient
    .get(endpoints.catalogOpsSchedules.scheduleRuns(scheduleId), {
      searchParams: toSearchParams({ ...params }),
    })
    .json<ScheduleRunsPageWire>();
  return toScheduleRunsPage(wire);
}

/** GET /runs — cross-schedule run feed, same shape as the per-schedule one with
 *  `scheduleId` / `scheduleName` populated. */
export async function getScheduleRunFeed(
  filters: ScheduleRunFeedFilters = {},
): Promise<ScheduleRunsPage> {
  const wire = await catalogOperationsClient
    .get(endpoints.catalogOpsSchedules.runs(), { searchParams: toSearchParams({ ...filters }) })
    .json<ScheduleRunsPageWire>();
  return toScheduleRunsPage(wire);
}

/**
 * GET /tree and GET /{id}/tree — FLOW → COORDINATOR → BATCH → SUB_BATCH
 * execution nodes. Products are deliberately not tree leaves (a run can have
 * millions); drill into them by passing a node's `executionId` as
 * `batchExecutionId` on the workflow-runs products endpoint.
 *
 * Returned untyped: the tree DTO is owned by the existing workflow-tracking
 * surface, and no schedules screen renders it yet.
 */
export async function getSchedulesTree(): Promise<unknown> {
  return catalogOperationsClient.get(endpoints.catalogOpsSchedules.tree()).json<unknown>();
}

export async function getScheduleTree(scheduleId: string): Promise<unknown> {
  return catalogOperationsClient
    .get(endpoints.catalogOpsSchedules.scheduleTree(scheduleId))
    .json<unknown>();
}
